/**
 * Keycloak Integration using keycloak-js
 * Wrapper around official Keycloak JavaScript adapter
 */

import Keycloak from "@/lib/keycloak.cjs.js";

let keycloakInstance = null;

/**
 * Initialize Keycloak instance with configuration from environment variables
 * @returns {Keycloak} Keycloak instance
 */
export function initializeKeycloakInstance() {
  if (!keycloakInstance) {
    keycloakInstance = new Keycloak({
      url: process.env.VUE_APP_KEYCLOAK_BASE_URL || "https://auth.bos-id.eu",
      realm: process.env.VUE_APP_KEYCLOAK_REALM || "bosid",
      clientId: process.env.VUE_APP_KEYCLOAK_CLIENT_ID || "bos-id-portal-prod",
    });
  }

  // console.log("Current URL:", window.location.href);
  // console.log("Keycloak instance:", keycloakInstance);
  return keycloakInstance;
}

async function initializeKeycloak(options) {
  if (!keycloakInstance) {
    throw Error("no keycloakInstance");
  }

  const initOptions = {
    onLoad: "login-required",
    ui_locales: "de-DE",
    flow: "standard",
    responseMode: "fragment",
    messageReceiveTimeout: 10000,
    pkceMethod: "S256",
    checkLoginIframe: false,
    redirectUri:
      process.env.VUE_APP_KEYCLOAK_REDIRECT_URI ||
      `${window.location.origin}/login-callback`,
  };

  const mergedOptions = Object.assign(initOptions, options);

  const authenticated = await keycloakInstance.init(mergedOptions);
  return authenticated;
}

/**
 * Get the current Keycloak instance
 * @returns {Keycloak|null} Keycloak instance or null if not initialized
 */
export function getKeycloak() {
  return keycloakInstance;
}

/**
 * Initiate Keycloak login flow
 * @param {string} email - Optional email to pre-fill login form
 */
export async function loginWithKeycloak(email = null) {
  // Immer frische Instanz, da ein Browser-Redirect folgt und eine evtl.
  // bereits initialisierte Instanz (z.B. durch restoreKeycloakSession)
  // sonst "can only be initialized once" werfen würde.
  keycloakInstance = null;
  initializeKeycloakInstance();

  // init() nur zur Initialisierung, kein automatischer Redirect
  await keycloakInstance.init({
    pkceMethod: "S256",
    checkLoginIframe: false,
    responseMode: "fragment",
  });

  // login() löst den Redirect mit loginHint aus
  await keycloakInstance.login({
    loginHint: email ?? undefined,
    redirectUri:
      process.env.VUE_APP_KEYCLOAK_REDIRECT_URI ||
      `${window.location.origin}/login-callback`,
    locale: "de-DE",
  });
}
export async function restoreKeycloakSession() {
  const token = sessionStorage.getItem("kc_token");
  const refreshToken = sessionStorage.getItem("kc_refresh_token");
  const idToken = sessionStorage.getItem("kc_id_token");

  if (!token || !refreshToken) return false;

  initializeKeycloakInstance();

  try {
    const authenticated = await keycloakInstance.init({
      pkceMethod: "S256",
      checkLoginIframe: false,
      responseMode: "fragment",
      token,
      refreshToken,
      idToken,
    });

    if (authenticated) {
      // Tokens nach Restore aktualisieren (könnten refreshed worden sein)
      sessionStorage.setItem("kc_token", keycloakInstance.token);
      sessionStorage.setItem("kc_refresh_token", keycloakInstance.refreshToken);
      sessionStorage.setItem("kc_id_token", keycloakInstance.idToken);
      return true;
    }
  } catch {
    // init() kann werfen wenn Session abgelaufen oder ungültig
  }

  // Instanz zurücksetzen, damit loginWithKeycloak eine frische init() durchführen kann
  keycloakInstance = null;
  return false;
}
/**
 * Handle Keycloak callback after authentication
 * Initializes Keycloak with authorization code flow
 * @returns {Promise<Object>} Object containing authenticated status and tokens
 */
export async function handleKeycloakCallback() {
  initializeKeycloakInstance();

  const authenticated = await keycloakInstance.init({
    ui_locales: "de-DE",
    flow: "standard",
    responseMode: "fragment",
    messageReceiveTimeout: 10000,
    pkceMethod: "S256",
    checkLoginIframe: false,
    token: sessionStorage.getItem("kc_token") ?? undefined,
    refreshToken: sessionStorage.getItem("kc_refresh_token") ?? undefined,
    idToken: sessionStorage.getItem("kc_id_token") ?? undefined,
  });

  if (authenticated) {
    sessionStorage.setItem("kc_token", keycloakInstance.token);
    sessionStorage.setItem("kc_refresh_token", keycloakInstance.refreshToken);
    sessionStorage.setItem("kc_id_token", keycloakInstance.idToken);
    return {
      authenticated: true,
      token: keycloakInstance.token,
      refreshToken: keycloakInstance.refreshToken,
      idToken: keycloakInstance.idToken,
      tokenParsed: keycloakInstance.tokenParsed,
    };
  } else {
    sessionStorage.removeItem("kc_token");
    sessionStorage.removeItem("kc_refresh_token");
    sessionStorage.removeItem("kc_id_token");
    throw new Error("Session abgelaufen, bitte neu anmelden");
  }
}

/**
 * Refresh Keycloak access token
 * @param {number} minValidity - Minimum token validity in seconds (default: 30)
 * @returns {Promise<Object>} Updated tokens
 */
export async function refreshKeycloakToken(minValidity = 30) {
  const keycloak = getKeycloak();

  if (!keycloak) {
    throw new Error("Keycloak not initialized");
  }

  try {
    const refreshed = await keycloak.updateToken(minValidity);

    if (refreshed) {
      return {
        token: keycloak.token,
        refreshToken: keycloak.refreshToken,
        idToken: keycloak.idToken,
      };
    } else {
      // Token is still valid
      return {
        token: keycloak.token,
        refreshToken: keycloak.refreshToken,
        idToken: keycloak.idToken,
      };
    }
  } catch (error) {
    console.error("Token refresh failed:", error);
    throw error;
  }
}

/**
 * Logout from Keycloak
 * @param {string} redirectUri - Optional redirect URI after logout
 */
export async function logoutKeycloak(redirectUri = null) {
  sessionStorage.removeItem("kc_token");
  sessionStorage.removeItem("kc_refresh_token");
  sessionStorage.removeItem("kc_id_token");
  const keycloak = getKeycloak();

  if (!keycloak) {
    return;
  }

  const options = {
    redirectUri:
      redirectUri ||
      process.env.VUE_APP_KEYCLOAK_REDIRECT_URI ||
      `${window.location.origin}/login-callback`,
  };

  // Flag setzen bevor die Seite verlassen wird
  localStorage.setItem("bos_logout_clean", "1");

  // Instanz zurücksetzen, bevor Keycloak die Seite weiterleitet
  keycloakInstance = null;

  await keycloak.logout(options);
}

/**
 * Check if user is authenticated with Keycloak
 * @returns {boolean} True if authenticated
 */
export function isKeycloakAuthenticated() {
  const keycloak = getKeycloak();
  return keycloak ? keycloak.authenticated : false;
}

/**
 * Get current access token
 * @returns {string|null} Access token or null
 */
export function getKeycloakToken() {
  const keycloak = getKeycloak();
  return keycloak ? keycloak.token : null;
}

/**
 * Get parsed token payload
 * @returns {Object|null} Parsed token or null
 */
export function getKeycloakTokenParsed() {
  const keycloak = getKeycloak();
  return keycloak ? keycloak.tokenParsed : null;
}
